import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useApp } from '../contexts/AppContext';
import useTranslation from '../hooks/useTranslation';
import { 
  SparklesIcon, 
  TrashIcon, 
  DocumentDuplicateIcon,
  EyeIcon, 
  EyeSlashIcon, 
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { 
  ColumnHeightOutlined, 
  ColumnWidthOutlined, 
  ExpandAltOutlined 
} from '@ant-design/icons';
import { applyFilterToImage } from '../utils/clutFilter';
import { validateAndRebuildImage, isValidImage, waitForImageLoad } from '../utils/imageValidation';
import { useThumbnailCache } from '../hooks/useThumbnailCache';
import { useImageReferenceMonitor } from '../hooks/useImageReferenceMonitor';
import EffectSelector from '../img-effects/components/EffectSelector';
import AdjustmentsControls from '../img-effects/adjustments/AdjustmentsControls';
import GenericEffectControls from './effects/GenericEffectControls';
import EffectActionBar from './effects/EffectActionBar';
import PaintingControls from './painting/PaintingControls';
import PaintingModal from './painting/PaintingModal';
import PaintingCanvas2 from './painting/PaintingCanvas2';
import CustomSlider from './CustomSlider';

// Componente de ícone de arrastar
const DragIcon = () => (
  <div className="flex flex-col space-y-1 cursor-grab active:cursor-grabbing">
    <div className="w-4 h-0.5 bg-gray-400"></div>
    <div className="w-4 h-0.5 bg-gray-400"></div>
    <div className="w-4 h-0.5 bg-gray-400"></div>
  </div>
);

// Componente de Configuração Personalizada de Cores
const ColorConfigDrawer = ({ onClose, onApply, colorBlendMode, colorOpacity, updateColorFilterSettings }) => {
  const { t } = useTranslation();
  const [color1, setColor1] = useState('#FF6B6B');
  const [color2, setColor2] = useState('#4ECDC4');
  const [color3, setColor3] = useState('#FFD93D');
  const [useThreeColors, setUseThreeColors] = useState(false);
  const [angle, setAngle] = useState(135);

  // Atualizar preview em tempo real no canvas
  const updatePreview = useCallback((newColor1, newColor2, newColor3, newUseThreeColors, newAngle) => {
    const colors = newUseThreeColors && newColor3 ? [newColor1, newColor2, newColor3] : [newColor1, newColor2];
    updateColorFilterSettings('gradient', {
      colors,
      angle: newAngle,
      opacity: colorOpacity,
      blendMode: colorBlendMode
    });
  }, [updateColorFilterSettings, colorOpacity, colorBlendMode]);

  // Atualizar preview quando qualquer controle muda
  useEffect(() => {
    updatePreview(color1, color2, color3, useThreeColors, angle);
  }, [color1, color2, color3, useThreeColors, angle, updatePreview]);

  const generateGradient = () => {
    const colors = useThreeColors && color3 ? [color1, color2, color3] : [color1, color2];
    return { colors, angle };
  };

  const handleApply = () => {
    const customFilter = {
      id: 'custom',
      name: t('colorGradients.custom'),
      colors: generateGradient().colors,
      angle: generateGradient().angle,
      type: 'gradient'
    };
    onApply(customFilter);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-80 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{t("colorGradients.customConfig")}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Cor 1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("colorGradients.color1")}</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={color1}
                onChange={(e) => setColor1(e.target.value)}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={color1}
                onChange={(e) => setColor1(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="#FF6B6B"
              />
            </div>
          </div>

          {/* Cor 2 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("colorGradients.color2")}</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={color2}
                onChange={(e) => setColor2(e.target.value)}
                className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={color2}
                onChange={(e) => setColor2(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="#4ECDC4"
              />
            </div>
          </div>

          {/* Cor 3 (opcional) */}
          <div>
            <label className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={useThreeColors}
                onChange={(e) => setUseThreeColors(e.target.checked)}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm font-medium text-gray-700">Usar terceira cor</span>
            </label>
            {useThreeColors && (
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={color3}
                  onChange={(e) => setColor3(e.target.value)}
                  className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={color3}
                  onChange={(e) => setColor3(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="#FFD93D"
                />
              </div>
            )}
          </div>

          {/* Ângulo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("colorGradients.angle")}: {angle}°
            </label>
            <input
              type="range"
              min="0"
              max="360"
              value={angle}
              onChange={(e) => setAngle(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
          >
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar de Controles com navegação por abas verticais
 */
const SidebarControls = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('geral');

  // Estados para drawers
  const [showFilters, setShowFilters] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtrosData, setFiltrosData] = useState(null);
  const [openCategories, setOpenCategories] = useState(new Set());
  const [applyingFilter, setApplyingFilter] = useState(false);
  // ✅ SOLUÇÃO 4: Cache limitado de thumbnails com LRU
  const thumbnailCache = useThumbnailCache(50); // Máximo de 50 thumbnails
  const [loadingThumbnails, setLoadingThumbnails] = useState(new Set());
  const [selectedFilter, setSelectedFilter] = useState(null);
  
  // Estados para navegação hierárquica de filtros
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filterDrawerDirection, setFilterDrawerDirection] = useState('forward');
  
  const [showColors, setShowColors] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [colorConfigOpen, setColorConfigOpen] = useState(false);
  const [showColorConfig, setShowColorConfig] = useState(false);
  const [activeColorFilterCanvas, setActiveColorFilterCanvas] = useState(null);
  
  const [showEffects, setShowEffects] = useState(false);
  const [effectsOpen, setEffectsOpen] = useState(false);
  // Navegação de efeitos: manter categoria aberta e sinal de volta
  const [lastOpenedCategory, setLastOpenedCategory] = useState(null);
  const [backToCategorySignal, setBackToCategorySignal] = useState(0);
  
  // Estados para modal de confirmação
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'filter' ou 'color'
  const [pendingTab, setPendingTab] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Ref para rastrear versão da imagem
  const lastImageVersionRef = useRef(null);
  
  // Estados para preview de filtro
  const [previewingFilter, setPreviewingFilter] = useState(null);
  const [originalImageBeforeFilter, setOriginalImageBeforeFilter] = useState(null);

  // Estados para pintura
  const [paintBrushSize, setPaintBrushSize] = useState(20);
  const [paintBrushColor, setPaintBrushColor] = useState('#000000');
  const [paintBrushOpacity, setPaintBrushOpacity] = useState(100);
  const [paintBrushFlow, setPaintBrushFlow] = useState(6);
  const [paintBrushSpacing, setPaintBrushSpacing] = useState(2);
  const [paintBrushAngle, setPaintBrushAngle] = useState(0);
  const [paintBrushRotate, setPaintBrushRotate] = useState(false);
  const [paintCurrentTool, setPaintCurrentTool] = useState('pen');
  const [paintStabilizerLevel, setPaintStabilizerLevel] = useState(10);
  const [paintStabilizerWeight, setPaintStabilizerWeight] = useState(50);
  const [paintCurrentBrushImage, setPaintCurrentBrushImage] = useState(null);
  const [paintBlendMode, setPaintBlendMode] = useState('source-over'); // Modo de mesclagem
  const [paintingTestModalOpen, setPaintingTestModalOpen] = useState(false); // 🧪 Modal de teste

  const {
    selectedImageId,
    uploadedImages,
    baseLayer,
    setSelectedImage,
    updateImage,
    updateImageOpacity,
    updateImageBlendMode,
    updateImageTransform,
    applyImageTransform, // Nova função para transformações pré-definidas
    duplicateImage,
    openDeleteConfirmation,
    setBaseLayerOpacity,
    setBaseLayerBackgroundColor,
    openAdvancedEditor,
    finalizeClipping,
    openEffectsPanel,
    setActiveEffectType,
    setIsPaperPanelOpen,
    saveHistoryState,
    // Estados para cores
    selectedColorFilter,
    setSelectedColorFilter,
    activeColorFilterType,
    setActiveColorFilterType,
    colorBlendMode,
    setColorBlendMode,
    colorOpacity,
    setColorOpacity,
    updateColorFilterSettings,
    applyColorFilter,
    openColorFiltersPanel,
    // Estados para efeitos
    activeEffectType,
    selectedEffect,
    setSelectedEffect,
    effectSettings,
    setEffectSettings,
    updateEffectSettings,
    resetEffectSettings,
    applyEffect,
    closeEffectsPanel,
    // Estados para pintura
    isPaintingActive,
    setIsPaintingActive,
    isSimplePaintingMode, // NOVO: Modo pintura simplificado
    setIsSimplePaintingMode, // NOVO: Modo pintura simplificado
    isPainting2Mode, // 🎨 NOVO: Modo Pintura2
    setIsPainting2Mode, // 🎨 NOVO: Modo Pintura2
    paintingSettings,
    setPaintingSettings,
  } = useApp();

  const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
  const isBaseLayerSelected = selectedImageId === 'base-layer';

  // Ativar/Desativar modo pintura2 automaticamente ao entrar/sair da aba
  useEffect(() => {
    // Ativar modo pintura2 quando entrar na aba de pintura2
    if (activeTab === 'pintura2' && selectedImageId && !isBaseLayerSelected && !isPainting2Mode) {
      setIsPainting2Mode(true);
    }
    // Desativar modo pintura2 ao sair da aba de pintura2
    if (isPainting2Mode && (activeTab !== 'pintura2' || !selectedImageId || isBaseLayerSelected)) {
      setIsPainting2Mode(false);
    }
  }, [activeTab, selectedImageId, isBaseLayerSelected, isPainting2Mode]);

  // Inicializar painel de efeitos quando mudar para aba de ajustes
  useEffect(() => {
    if (activeTab === 'ajustes' && selectedImageId && !isBaseLayerSelected) {
      openEffectsPanel(selectedImageId);
      setActiveEffectType('adjustments');
    }
  }, [activeTab, selectedImageId, isBaseLayerSelected, openEffectsPanel, setActiveEffectType]);

  // Inicializar painel de efeitos quando mudar para aba de efeitos
  useEffect(() => {
    if (activeTab === 'efeitos' && selectedImageId && !isBaseLayerSelected) {
      openEffectsPanel(selectedImageId);
      // Não definir tipo específico, deixar null para mostrar seletor
      if (activeEffectType === 'adjustments') {
        setActiveEffectType(null);
      }
    }
  }, [activeTab, selectedImageId, isBaseLayerSelected, openEffectsPanel, setActiveEffectType, activeEffectType]);

  const handleImageTransform = (type) => {
    if (selectedImageId && !isBaseLayerSelected) {
      // 🔧 CORRIGIDO: Usar applyImageTransform ao invés de updateImageTransform
      // applyImageTransform calcula a transformação correta baseada no tipo
      console.log('🔘 [SidebarControls] Aplicando transformação:', type);
      applyImageTransform(selectedImageId, type);
    }
  };

  const handleDuplicateLayer = () => {
    if (selectedImageId && !isBaseLayerSelected) {
      duplicateImage(selectedImageId);
    }
  };

  const handleDeleteLayer = () => {
    if (selectedImageId && !isBaseLayerSelected) {
      openDeleteConfirmation(selectedImageId);
    }
  };

  // Função para verificar conflitos e mostrar modal de confirmação
  const checkForConflictsAndShowModal = (newTab, action) => {
    const hasActiveColorFilter = selectedColorFilter !== null;
    const hasPreviewingFilter = previewingFilter !== null;
    const hasActiveEffect = activeEffectType !== null;
    
    // Se está tentando sair de filtros para outra aba e tem filtro em preview
    if (activeTab === 'filtros' && newTab !== 'filtros' && hasPreviewingFilter) {
      setConfirmationType('filter');
      setPendingTab(newTab);
      setPendingAction(action);
      setShowConfirmationModal(true);
      return true;
    }
    
    // Se está tentando sair de cores para outra aba e tem cor ativa
    if (activeTab === 'cores' && newTab !== 'cores' && hasActiveColorFilter) {
      setConfirmationType('color');
      setPendingTab(newTab);
      setPendingAction(action);
      setShowConfirmationModal(true);
      return true;
    }
    
    // Se está tentando sair de efeitos para outra aba e tem efeito ativo
    if (activeTab === 'efeitos' && newTab !== 'efeitos' && hasActiveEffect) {
      setConfirmationType('effect');
      setPendingTab(newTab);
      setPendingAction(action);
      setShowConfirmationModal(true);
      return true;
    }
    
    return false;
  };

  // Função para confirmar ação (Sim = Aplicar)
  const confirmAction = () => {
    if (confirmationType === 'filter') {
      // Manter o filtro aplicado (já está aplicado no preview)
      setPreviewingFilter(null);
      setOriginalImageBeforeFilter(null);
      saveHistoryState();
      
      // Invalidar thumbnails após confirmar filtro
      console.log('Filtro confirmado, invalidando thumbnails...');
      thumbnailCache.clear();
      setLoadingThumbnails(new Set());
      setOpenCategories(new Set()); // Fechar todas categorias
    } else if (confirmationType === 'color') {
      // Aplicar filtro de cor selecionado
      if (selectedColorFilter) {
        handleApplySelectedFilter();
      }
      setSelectedColorFilter(null);
      setActiveColorFilterCanvas(null);
      
      // Invalidar thumbnails após confirmar cor
      console.log('Cor confirmada, invalidando thumbnails...');
      thumbnailCache.clear();
      setLoadingThumbnails(new Set());
      setOpenCategories(new Set()); // Fechar todas categorias
    } else if (confirmationType === 'effect') {
      // Aplicar efeito selecionado
      applyEffect();
      setActiveEffectType(null);
      
      // Invalidar thumbnails após confirmar efeito
      console.log('Efeito confirmado, invalidando thumbnails...');
      thumbnailCache.clear();
      setLoadingThumbnails(new Set());
      setOpenCategories(new Set()); // Fechar todas categorias
    }
    
    // Fechar modal e executar ação pendente
    setShowConfirmationModal(false);
    if (pendingAction) {
      pendingAction();
    }
  };

  // Função para cancelar ação (Não = Cancelar)
  const cancelAction = () => {
    if (confirmationType === 'filter') {
      // Reverter para imagem original
      if (originalImageBeforeFilter && selectedImageId) {
        updateImage(selectedImageId, originalImageBeforeFilter);
      }
      setPreviewingFilter(null);
      setOriginalImageBeforeFilter(null);
    } else if (confirmationType === 'color') {
      // Limpar seleções sem aplicar
      setSelectedColorFilter(null);
      setActiveColorFilterCanvas(null);
    } else if (confirmationType === 'effect') {
      // Limpar efeito sem aplicar
      setActiveEffectType(null);
    }
    
    // Fechar modal e executar ação pendente
    setShowConfirmationModal(false);
    if (pendingAction) {
      pendingAction();
    }
  };

  // Se estiver selecionada a base layer na aba Ajustes/Filtros/Cores/Efeitos, voltar para Geral
  React.useEffect(() => {
    if (isBaseLayerSelected && activeTab !== 'geral') {
      setActiveTab('geral');
    }
  }, [isBaseLayerSelected, activeTab]);

  // Effects para controlar os drawers
  useEffect(() => {
    if (filtersOpen) {
      const t = setTimeout(() => setShowFilters(true), 0);
      return () => clearTimeout(t);
    } else {
      setShowFilters(false);
    }
  }, [filtersOpen]);

  useEffect(() => {
    if (colorsOpen) {
      const t = setTimeout(() => setShowColors(true), 0);
      return () => clearTimeout(t);
    } else {
      setShowColors(false);
    }
  }, [colorsOpen]);

  useEffect(() => {
    if (effectsOpen) {
      const t = setTimeout(() => setShowEffects(true), 0);
      return () => clearTimeout(t);
    } else {
      setShowEffects(false);
    }
  }, [effectsOpen]);

  // Gerar thumbnails para categorias de filtros quando filtros são exibidos
  useEffect(() => {
    if (showFilters && filtrosData && selectedImageId && uploadedImages.find(img => img.id === selectedImageId)) {
      // Gerar thumbnail do primeiro filtro de cada categoria para preview
      filtrosData.categorias
        .filter((categoria) => categoria.nome.toLowerCase() !== 'none')
        .forEach((categoria) => {
          const firstFilter = categoria.filtros[0];
          if (firstFilter) {
            generateFilterThumbnail(firstFilter, categoria.nome);
          }
        });
    }
  }, [showFilters, filtrosData, selectedImageId, uploadedImages]);

  // Gerar thumbnails de todos os filtros quando uma categoria é selecionada
  useEffect(() => {
    if (selectedFilterCategory && showFilterDrawer && selectedImageId && uploadedImages.find(img => img.id === selectedImageId)) {
      selectedFilterCategory.filtros.forEach(filtro => {
        generateFilterThumbnail(filtro, selectedFilterCategory.nome);
      });
    }
  }, [selectedFilterCategory, showFilterDrawer, selectedImageId, uploadedImages]);

  // Limpar cache de thumbnails quando a imagem selecionada mudar
  useEffect(() => {
    thumbnailCache.clear();
    setLoadingThumbnails(new Set());
  }, [selectedImageId]);

  // ✅ SOLUÇÃO 2: Cleanup automático de estados de preview
  useEffect(() => {
    // Limpar preview quando imagem selecionada mudar
    if (originalImageBeforeFilter) {
      const prevImageId = originalImageBeforeFilter.id;
      if (prevImageId !== selectedImageId) {
        console.log('🧹 Limpando estados de preview - imagem mudou');
        setPreviewingFilter(null);
        setOriginalImageBeforeFilter(null);
        setSelectedFilter(null);
      }
    }
  }, [selectedImageId, originalImageBeforeFilter]);

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      // Limpar estados ao desmontar
      setPreviewingFilter(null);
      setOriginalImageBeforeFilter(null);
      setSelectedFilter(null);
    };
  }, []);

  // ✅ SOLUÇÃO 3: Tratamento de eventos de visibilidade (troca de aba)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Aba oculta - opcional: pausar operações
        console.log('👁️ Tab hidden - pausing operations');
      } else {
        // Aba visível - validar e reconstruir referências
        console.log('👁️ Tab visible - validating image references');
        
        // Validar imagem selecionada atual
        if (selectedImageId && selectedImageId !== 'base-layer') {
          const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
          if (selectedImage) {
            validateAndRebuildImage(selectedImage, { timeout: 3000 })
              .then(validatedImage => {
                if (validatedImage && validatedImage !== selectedImage.image) {
                  console.log('✅ Reconstruída referência de imagem após retorno da aba');
                  updateImage(selectedImageId, { image: validatedImage });
                }
              })
              .catch(error => {
                console.warn('⚠️ Falha ao validar imagem após retorno da aba:', error);
              });
          }
        }
      }
    };
    
    const handlePageHide = () => {
      // Limpar estados sensíveis antes de descarregar
      console.log('📄 Page hide - clearing preview states');
      setPreviewingFilter(null);
      setOriginalImageBeforeFilter(null);
      setSelectedFilter(null);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [uploadedImages, selectedImageId, updateImage]);

  // ✅ SOLUÇÃO 8: Monitoramento e logging de referências
  // Apenas em desenvolvimento ou se explicitamente habilitado
  const shouldMonitor = process.env.NODE_ENV === 'development' || 
    (typeof window !== 'undefined' && window.__ENABLE_IMAGE_MONITORING === true);
  
  useImageReferenceMonitor(
    shouldMonitor ? uploadedImages : [], 
    shouldMonitor ? selectedImageId : null, 
    shouldMonitor ? 10000 : 60000 // Se desabilitado, verificar muito raramente
  );

  // Funções para filtros analógicos
  const generateFilterThumbnail = async (filtro, categoryName) => {
    const thumbnailKey = `${categoryName}-${filtro.caminho}`;

    // ✅ SOLUÇÃO 4: Usar cache LRU
    const cachedThumbnail = thumbnailCache.get(thumbnailKey);
    if (cachedThumbnail) {
      console.log('📦 Thumbnail em cache:', thumbnailKey);
      return;
    }
    
    if (loadingThumbnails.has(thumbnailKey)) {
      console.log('⏳ Thumbnail já está sendo gerado:', thumbnailKey);
      return;
    }

    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) {
      console.log('⚠️ generateFilterThumbnail: Imagem não encontrada');
      return;
    }

    // ✅ SOLUÇÃO 6: Validar imagem antes de gerar thumbnail
    if (!selectedImage.image) {
      console.warn('⚠️ generateFilterThumbnail: Imagem sem referência, tentando reconstruir');
      
      // Tentar reconstruir
      try {
        const rebuiltImage = await validateAndRebuildImage(selectedImage, { timeout: 5000 });
        if (rebuiltImage) {
          // Atualizar referência
          updateImage(selectedImageId, { image: rebuiltImage });
          selectedImage.image = rebuiltImage;
        } else {
          console.error('❌ generateFilterThumbnail: Não foi possível reconstruir imagem');
          return;
        }
      } catch (error) {
        console.error('❌ generateFilterThumbnail: Erro ao reconstruir:', error);
        return;
      }
    }

    // Validar se Image é instância válida
    if (!(selectedImage.image instanceof HTMLImageElement)) {
      console.error('❌ generateFilterThumbnail: Image não é HTMLImageElement');
      return;
    }

    // Validar se Image está completo
    if (!selectedImage.image.complete) {
      console.warn('⚠️ generateFilterThumbnail: Image não está completo, aguardando...');
      try {
        await waitForImageLoad(selectedImage.image, 5000);
      } catch (error) {
        console.error('❌ generateFilterThumbnail: Timeout ao aguardar carregamento:', error);
        return;
      }
    }

    // Verificar dimensões
    if (selectedImage.image.naturalWidth === 0 || selectedImage.image.naturalHeight === 0) {
      console.error('❌ generateFilterThumbnail: Imagem tem dimensões inválidas');
      return;
    }

    // Verificar se a imagem mudou durante a validação
    const currentImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!currentImage || currentImage.image !== selectedImage.image) {
      console.warn('⚠️ generateFilterThumbnail: Imagem mudou durante validação');
      return;
    }

    console.log('🖼️ Gerando thumbnail:', categoryName, '-', filtro.nome, 'para imagem ID:', selectedImageId);

    setLoadingThumbnails(prev => new Set(prev).add(thumbnailKey));

    try {
      const clutUrl = `/lotie/${filtro.caminho}`;
      // ✅ Usar a imagem validada
      const thumbnail = await applyFilterToImage(selectedImage.image, clutUrl, 1.0);
      
      // ✅ SOLUÇÃO 4: Salvar no cache LRU
      thumbnailCache.set(thumbnailKey, thumbnail);
      
      console.log('Thumbnail gerado:', thumbnailKey);
    } catch (error) {
      console.error('Erro ao gerar thumbnail:', thumbnailKey, error);
    } finally {
      setLoadingThumbnails(prev => {
        const newSet = new Set(prev);
        newSet.delete(thumbnailKey);
        return newSet;
      });
    }
  };

  const toggleCategory = (categoryName) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
        // Gerar thumbnails quando abrir categoria
        if (filtrosData) {
          const categoria = filtrosData.categorias.find(cat => cat.nome === categoryName);
          if (categoria) {
            categoria.filtros.forEach(filtro => {
              generateFilterThumbnail(filtro, categoryName);
            });
          }
        }
      }
      return newSet;
    });
  };

  const handleApplyFilter = async (filtro) => {
    if (!selectedImageId || selectedImageId === 'base-layer') return;

    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) {
      console.error('❌ handleApplyFilter: Imagem selecionada não encontrada');
      return;
    }

    console.log('Aplicando filtro:', filtro.nome, 'Caminho:', filtro.caminho);

    setApplyingFilter(true);

    try {
      // ✅ SOLUÇÃO 5: Validar e reconstruir referência de Image antes de aplicar filtro
      let validImage = await validateAndRebuildImage(selectedImage, { 
        timeout: 10000,
        preferOriginalSrc: false 
      });

      if (!validImage) {
        console.error('❌ handleApplyFilter: Não foi possível validar/reconstruir a imagem');
        setApplyingFilter(false);
        alert('Erro ao validar imagem. Tente novamente.');
        return;
      }

      // Se a imagem foi reconstruída, atualizar referência
      if (validImage !== selectedImage.image) {
        console.log('✅ handleApplyFilter: Imagem reconstruída, atualizando referência');
        updateImage(selectedImageId, { image: validImage });
        // Atualizar selectedImage local
        selectedImage.image = validImage;
      }

      // Aguardar carregamento completo se necessário
      if (!validImage.complete) {
        console.warn('⚠️ handleApplyFilter: Aguardando carregamento completo da imagem');
        try {
          await waitForImageLoad(validImage, 10000);
        } catch (error) {
          console.error('❌ handleApplyFilter: Timeout ao aguardar carregamento:', error);
          setApplyingFilter(false);
          alert('Timeout ao aguardar carregamento da imagem. Tente novamente.');
          return;
        }
      }

      // Verificar dimensões
      if (validImage.naturalWidth === 0 || validImage.naturalHeight === 0) {
        console.error('❌ handleApplyFilter: Imagem tem dimensões inválidas');
        setApplyingFilter(false);
        alert('Imagem com dimensões inválidas. Tente novamente.');
        return;
      }

      // Salvar estado original antes de aplicar (se ainda não foi salvo)
      if (!previewingFilter) {
        setOriginalImageBeforeFilter({
          ...selectedImage,
          image: validImage, // Usar imagem validada
          originalImage: selectedImage.originalImage || validImage, // Preservar original
          analogFilter: selectedImage.analogFilter,
          analogFilterPath: selectedImage.analogFilterPath
        });
      }
      
      const clutUrl = `/lotie/${filtro.caminho}`;
      console.log('🔗 URL do filtro:', clutUrl);
      
      // ✅ Usar imagem validada (originalImage se existir, senão a validada)
      const sourceImage = selectedImage.originalImage || validImage;
      
      // Validar sourceImage também antes de aplicar filtro
      let validSourceImage = sourceImage;
      if (sourceImage !== validImage) {
        // Se sourceImage é diferente, validar também
        if (!isValidImage(sourceImage)) {
          console.warn('⚠️ handleApplyFilter: sourceImage inválida, usando validImage');
          validSourceImage = validImage;
        }
      }
      
      const filteredImageUrl = await applyFilterToImage(validSourceImage, clutUrl, 1.0);
      console.log('Filtro aplicado com sucesso');
      
      // Criar nova imagem com o filtro aplicado
      const img = new Image();
      img.onload = () => {
        updateImage(selectedImageId, {
          ...selectedImage,
          image: img,
          originalImage: selectedImage.originalImage || selectedImage.image, // Preservar original
          analogFilter: filtro.nome,
          analogFilterPath: filtro.caminho
        });
        setPreviewingFilter(filtro);
        setSelectedFilter(filtro);
        setApplyingFilter(false);
        console.log('Filtro em preview');
        
        // 📋 LOG: Registrar aplicação do filtro CLUT
        if (window.__logAction) {
          window.__logAction('APPLY_CLUT_FILTER', selectedImageId, {
            filterName: filtro.nome,
            filterPath: filtro.caminho,
            category: filtro.categoria || 'unknown'
          });
        }
      };
      img.onerror = () => {
        console.error('Erro ao carregar imagem filtrada');
        setApplyingFilter(false);
      };
      img.src = filteredImageUrl;
    } catch (error) {
      console.error('Erro ao aplicar filtro:', error);
      alert('Erro ao aplicar filtro. Verifique o console para detalhes.');
      setApplyingFilter(false);
    }
  };

  // Funções para filtros de cores
  const generateColorFilterPreview = async (colorFilter, imageId) => {
    const image = uploadedImages.find(img => img.id === imageId);
    if (!image || !image.image) return null;

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Tamanho menor para preview
      const maxSize = 200;
      const scale = Math.min(maxSize / image.image.width, maxSize / image.image.height);
      
      canvas.width = image.image.width * scale;
      canvas.height = image.image.height * scale;
      
      // Desenhar imagem original
      ctx.drawImage(image.image, 0, 0, canvas.width, canvas.height);
      
      // Aplicar filtro de cor
      if (colorFilter.type === 'gradient') {
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        colorFilter.colors.forEach((color, index) => {
          gradient.addColorStop(index / (colorFilter.colors.length - 1), color);
        });
        
        ctx.globalCompositeOperation = colorFilter.blendMode || 'multiply';
        ctx.globalAlpha = (colorFilter.opacity || 50) / 100;
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      return null;
    }
  };

  const handleApplySelectedFilter = async () => {
    if (!selectedImageId || selectedImageId === 'base-layer' || !selectedColorFilter) return;
    
    const image = uploadedImages.find(img => img.id === selectedImageId);
    if (!image) return;

    try {
      setApplyingFilter(true);
      
      console.log('Debug handleApplySelectedFilter:', {
        selectedColorFilter,
        activeColorFilterCanvas,
        isCanvas: activeColorFilterCanvas instanceof HTMLCanvasElement
      });
      
      // Passar o canvas diretamente para applyColorFilter
      await applyColorFilter(activeColorFilterCanvas);
      
      // Fechar drawer e limpar seleção
      setShowColors(false);
      setTimeout(() => setColorsOpen(false), 250);
      setSelectedColorFilter(null);
      setActiveColorFilterCanvas(null);
    } catch (error) {
      console.error('Erro ao aplicar filtro de cor:', error);
    } finally {
      setApplyingFilter(false);
    }
  };

  const closeColorFiltersPanel = () => {
    setActiveColorFilterType(null);
    closeEffectsPanel();
    setColorsOpen(false);
    setShowColors(false);
  };

  // Constantes para filtros de cores
  const COLOR_FILTERS = [
    { id: 'sunset', name: t('colorGradients.sunset'), colors: ['#FF6B6B', '#FFE66D'], angle: 135 },
    { id: 'ocean', name: t('colorGradients.ocean'), colors: ['#667eea', '#764ba2'], angle: 135 },
    { id: 'forest', name: t('colorGradients.forest'), colors: ['#0F2027', '#203A43', '#2C5364'], angle: 135 },
    { id: 'purple-dream', name: t('colorGradients.purpleDream'), colors: ['#C471F5', '#FA71CD'], angle: 135 },
    { id: 'fire', name: t('colorGradients.fire'), colors: ['#f12711', '#f5af19'], angle: 135 },
    { id: 'mint', name: t('colorGradients.mint'), colors: ['#00b09b', '#96c93d'], angle: 135 },
    { id: 'rose', name: t('colorGradients.rose'), colors: ['#f857a6', '#ff5858'], angle: 135 },
    { id: 'sky', name: t('colorGradients.sky'), colors: ['#0093E9', '#80D0C7'], angle: 135 },
    { id: 'gold', name: t('colorGradients.gold'), colors: ['#F09819', '#EDDE5D'], angle: 135 },
    { id: 'sunset-dark', name: t('colorGradients.sunsetDark'), colors: ['#355C7D', '#6C5B7B', '#C06C84'], angle: 135 },
    { id: 'blue-raspberry', name: t('colorGradients.blueRaspberry'), colors: ['#00d2ff', '#3a47d5'], angle: 135 },
    { id: 'peach', name: t('colorGradients.peach'), colors: ['#ED4264', '#FFEDBC'], angle: 135 }
  ];

  const handleSelectColorFilter = (colorFilter) => {
    setSelectedColorFilter(colorFilter);
    
    // Usar novo sistema com lazy loading
    // Abrir o painel de filtros de cores e configurar o gradiente
    if (selectedImageId && selectedImageId !== 'base-layer') {
      openColorFiltersPanel(selectedImageId);
      setActiveColorFilterType('gradient');
      
      // Configurar as cores do gradiente selecionado
      updateColorFilterSettings('gradient', {
        colors: colorFilter.colors,
        angle: colorFilter.angle || 135,
        opacity: colorOpacity,
        blendMode: colorBlendMode
      });
    }
  };

  // Carregar dados dos filtros analógicos
  useEffect(() => {
    const loadFiltrosData = async () => {
      try {
        const response = await fetch('/lotie/filtros.json');
        if (response.ok) {
          const data = await response.json();
          setFiltrosData(data);
        }
      } catch (error) {
        console.error('Erro ao carregar dados dos filtros:', error);
      }
    };

    loadFiltrosData();
  }, []);

  // Resetar filtro selecionado e thumbnails quando mudar de imagem
  useEffect(() => {
    setSelectedFilter(null);
    thumbnailCache.clear();
    setLoadingThumbnails(new Set());
    lastImageVersionRef.current = null;
  }, [selectedImageId]);

  return (
    <div className="h-full flex overflow-hidden rounded-2xl">
      {/* Navbar Vertical - Abas à esquerda */}
      <div className="w-14 flex flex-col rounded-l-2xl">
        {/* Aba Geral */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('geral', () => setActiveTab('geral'));
            if (!hasConflict) {
              setActiveTab('geral');
            }
          }}
          disabled={!selectedImageId}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.general')}
        >
          <svg className="w-6 h-6" fill="none" stroke={activeTab === 'geral' ? '#96c925' : 'rgba(255, 255, 255, 0.4)'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <span className={`text-[10px] font-medium ${activeTab === 'geral' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.general')}</span>
        </button>

        {/* Aba Ajustes */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('ajustes', () => setActiveTab('ajustes'));
            if (!hasConflict) {
              setActiveTab('ajustes');
            }
          }}
          disabled={!selectedImageId || isBaseLayerSelected}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.adjustments')}
        >
          <svg className="w-6 h-6" fill="none" stroke={activeTab === 'ajustes' ? '#96c925' : 'rgba(255, 255, 255, 0.4)'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          <span className={`text-[10px] font-medium ${activeTab === 'ajustes' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.adjustments')}</span>
        </button>

        {/* Aba Filtros */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('filtros', () => setActiveTab('filtros'));
            if (!hasConflict) {
              setActiveTab('filtros');
            }
          }}
          disabled={!selectedImageId || isBaseLayerSelected}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.filters')}
        >
          <svg className="w-6 h-6" fill="none" stroke={activeTab === 'filtros' ? '#96c925' : 'rgba(255, 255, 255, 0.4)'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className={`text-[10px] font-medium ${activeTab === 'filtros' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.filters')}</span>
        </button>

        {/* Aba Cores */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('cores', () => setActiveTab('cores'));
            if (!hasConflict) {
              setActiveTab('cores');
            }
          }}
          disabled={!selectedImageId || isBaseLayerSelected}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.colors')}
        >
          <svg className="w-6 h-6" fill="none" stroke={activeTab === 'cores' ? '#96c925' : 'rgba(255, 255, 255, 0.4)'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          <span className={`text-[10px] font-medium ${activeTab === 'cores' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.colors')}</span>
        </button>

        {/* Aba Efeitos */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('efeitos', () => setActiveTab('efeitos'));
            if (!hasConflict) {
              setActiveTab('efeitos');
            }
          }}
          disabled={!selectedImageId || isBaseLayerSelected}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.effects')}
        >
          <SparklesIcon className="w-6 h-6" style={{ color: activeTab === 'efeitos' ? '#96c925' : 'rgba(255, 255, 255, 0.4)' }} />
          <span className={`text-[10px] font-medium ${activeTab === 'efeitos' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.effects')}</span>
        </button>

        {/* Aba Pintura2 */}
        <button
          onClick={() => {
            const hasConflict = checkForConflictsAndShowModal('pintura2', () => setActiveTab('pintura2'));
            if (!hasConflict) {
              setActiveTab('pintura2');
            }
          }}
          disabled={!selectedImageId || isBaseLayerSelected}
          className="flex flex-col items-center gap-1 py-2.5 px-1 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title={t('tabs.painting2')}
        >
          <svg className="w-6 h-6" fill="none" stroke={activeTab === 'pintura2' ? '#96c925' : 'rgba(255, 255, 255, 0.4)'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z M9 12h6m-6 4h6" />
          </svg>
          <span className={`text-[10px] font-medium ${activeTab === 'pintura2' ? 'text-[#96c925]' : 'text-white/40'}`}>{t('tabs.painting2')}</span>
        </button>

      </div>

      {/* Área de Conteúdo - muda conforme aba */}
      <div className="flex-1 flex flex-col overflow-hidden rounded-r-2xl">

        {/* Conteúdo da aba */}
        <div className="flex-1 overflow-y-auto sidebar-scrollbar">
          {!selectedImageId ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <svg className="w-16 h-16 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-gray-600 font-medium">{t('layers.noLayerSelected')}</p>
              <p className="text-xs text-gray-500 mt-1">{t('layers.selectLayerToEdit')}</p>
            </div>
          ) : (
            <>
              {/* ABA GERAL */}
              {activeTab === 'geral' && (
                <div className="space-y-4 p-4">
                  {isBaseLayerSelected ? (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Cor de Fundo</label>
                        <div className="flex items-center space-x-2.5">
                          <input type="color" value={baseLayer.backgroundColor || '#ffffff'} onChange={(e) => setBaseLayerBackgroundColor(e.target.value)} className="w-10 h-7 border border-gray-300 rounded cursor-pointer" />
                          <input type="text" value={baseLayer.backgroundColor || '#ffffff'} onChange={(e) => setBaseLayerBackgroundColor(e.target.value)} className="flex-1 input-xs" placeholder="#ffffff" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-white mb-1.5">Opacidade: {baseLayer.opacity}%</label>
                        <input type="range" min="0" max="100" value={baseLayer.opacity} onChange={(e) => setBaseLayerOpacity(parseInt(e.target.value))} className="w-full h-0.5 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                      </div>
                      
                      {/* Botão para configurar tamanho da folha */}
                      <div className="border-t border-gray-200 my-4"></div>
                      <button
                        onClick={() => setIsPaperPanelOpen(true)}
                        className="w-full flex items-center gap-3 p-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors group"
                        title="Configurar tamanho da folha"
                      >
                        <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          Configurar Tamanho
                        </span>
                      </button>
                    </>
                  ) : selectedImage ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-white mb-1.5">Opacidade: {selectedImage.opacity || 100}%</label>
                        <input type="range" min="0" max="100" value={selectedImage.opacity || 100} onChange={(e) => updateImageOpacity(selectedImageId, parseInt(e.target.value))} className="w-full h-0.5 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-white mb-1.5">{t('layers.blendMode')}</label>
                        <div className="relative">
                          <select 
                            value={selectedImage.blendMode || 'normal'} 
                            onChange={(e) => updateImageBlendMode(selectedImageId, e.target.value)} 
                            className="w-full appearance-none bg-black/30 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 hover:bg-black/40 transition-colors"
                          >
                            <option value="normal" className="bg-gray-800">Normal</option>
                            <option value="multiply" className="bg-gray-800">Multiplicar</option>
                            <option value="screen" className="bg-gray-800">Tela</option>
                            <option value="overlay" className="bg-gray-800">Sobreposição</option>
                            <option value="soft-light" className="bg-gray-800">Luz Suave</option>
                            <option value="hard-light" className="bg-gray-800">Luz Forte</option>
                            <option value="color-dodge" className="bg-gray-800">Subexposição de Cor</option>
                            <option value="color-burn" className="bg-gray-800">Superexposição de Cor</option>
                            <option value="darken" className="bg-gray-800">Escurecer</option>
                            <option value="lighten" className="bg-gray-800">Clarear</option>
                            <option value="difference" className="bg-gray-800">Diferença</option>
                            <option value="exclusion" className="bg-gray-800">Exclusão</option>
                          </select>
                          {/* Ícone de seta customizado */}
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-white mb-2">{t('resize.title')}</label>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => handleImageTransform('fillHeight')} className="flex items-center justify-center p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors group relative" title={t("transforms.fillHeight")}>
                            <ColumnHeightOutlined className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleImageTransform('fillWidth')} className="flex items-center justify-center p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors group relative" title={t("transforms.fillWidth")}>
                            <ColumnWidthOutlined className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleImageTransform('fillAll')} className="flex items-center justify-center p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors group relative" title={t("transforms.fillAll")}>
                            <ExpandAltOutlined className="w-4 h-4" />
                          </button>
                          <div className="w-px h-6 bg-gray-300 mx-1" />
                          <button onClick={handleDuplicateLayer} className="flex items-center justify-center p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors group relative" title="Duplicar Camada">
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          </button>
                          <button onClick={handleDeleteLayer} className="flex items-center justify-center p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors group relative" title="Excluir Camada">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Botões de Recorte */}
                      {selectedImage.backgroundRemoved && !selectedImage.clippingFinalized && (
                        <div className="space-y-2">
                          <button
                            onClick={() => openAdvancedEditor(selectedImageId)}
                            className="w-full flex items-center justify-center space-x-2 p-3 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                            title="Refinar recorte"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="text-sm font-medium">Refinar Recorte</span>
                          </button>

                          <button
                            onClick={() => finalizeClipping(selectedImageId)}
                            className="w-full flex items-center justify-center space-x-2 p-3 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                            title="Finalizar recorte"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm font-medium">Finalizar Recorte</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* ABA AJUSTES */}
              {activeTab === 'ajustes' && selectedImage && !isBaseLayerSelected && (
                <div className="h-full overflow-y-auto p-0">
                  <AdjustmentsControls
                    settings={effectSettings?.adjustments || {}}
                    onUpdate={(newSettings) => {
                      console.log('Atualizando ajustes:', newSettings);
                      updateEffectSettings('adjustments', newSettings);
                    }}
                    onApply={() => {
                      console.log('Aplicando ajustes');
                      applyEffect();
                      
                      // Invalidar thumbnails após aplicar ajustes permanentemente
                      console.log('Ajustes aplicados permanentemente, invalidando thumbnails...');
                      thumbnailCache.clear();
                      setLoadingThumbnails(new Set());
                      setOpenCategories(new Set()); // Fechar todas categorias
                    }}
                    onBack={() => {
                      setActiveEffectType(null);
                      closeEffectsPanel();
                    }}
                    hideBackButton={true}
                  />
                </div>
              )}

              {/* ABA FILTROS */}
              {activeTab === 'filtros' && selectedImage && !isBaseLayerSelected && (
                <div className="h-full flex flex-col relative overflow-hidden">
                  {/* Loading overlay ao aplicar filtro */}
                  {applyingFilter && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-4 shadow-xl">
                        <div className="flex items-center space-x-3">
                          <svg className="animate-spin h-5 w-5" style={{ color: '#96c925' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-sm font-medium text-gray-700">Aplicando filtro...</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Área scrollável de filtros - Lista de Categorias */}
                  <div className={`flex-1 overflow-y-auto transition-transform duration-300 ease-in-out ${
                    showFilterDrawer ? '-translate-x-full pointer-events-none' : 'translate-x-0 pointer-events-auto'
                  }`}>
                    {/* Lista de Categorias */}
                    {filtrosData ? (
                      <div>
                        {filtrosData.categorias
                          .filter((categoria) => categoria.nome.toLowerCase() !== 'none')
                          .map((categoria) => {
                            const firstFilter = categoria.filtros[0];
                            const thumbnailKey = firstFilter ? `${categoria.nome}-${firstFilter.caminho}` : null;
                            const thumbnailUrl = thumbnailKey ? thumbnailCache.get(thumbnailKey) : null;
                            const isLoading = thumbnailKey ? loadingThumbnails.has(thumbnailKey) : false;
                            const fallbackImage = selectedImage ? selectedImage.src : null;
                            const previewImage = thumbnailUrl || (isLoading ? null : fallbackImage);

                        return (
                            <button
                                key={categoria.nome}
                                onClick={() => {
                                  setSelectedFilterCategory(categoria);
                                  setFilterDrawerDirection('forward');
                                  setShowFilterDrawer(false);
                                  // Usar requestAnimationFrame para garantir que o drawer seja renderizado fora da tela antes de animar
                                  requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                      setShowFilterDrawer(true);
                                    });
                                  });
                                }}
                                className="w-full px-3 py-2.5 transition-colors group hover:opacity-90"
                              >
                                {previewImage ? (
                                  <div className="relative w-full">
                                    <img 
                                      src={previewImage} 
                                      alt={categoria.nome} 
                                      className="w-full object-cover border" 
                                      style={{ 
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        height: '77px',
                                        minHeight: '77px',
                                        borderRadius: '12px'
                                      }} 
                                    />
                                    <div className="absolute inset-x-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/70 via-black/50 to-transparent" style={{ borderRadius: '0 0 12px 12px', paddingTop: '20px' }}>
                                      <p className="font-semibold text-white drop-shadow-lg" style={{ fontSize: '13px', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                                  {categoria.nome}
                                      </p>
                              </div>
                                  </div>
                                ) : (
                                    <div className="w-full flex items-center justify-center" style={{ 
                                      height: '77px',
                                      minHeight: '77px',
                                      backgroundColor: '#28380f',
                                      borderRadius: '12px'
                                    }}>
                                    <p className="font-semibold text-white" style={{ fontSize: '13px' }}>
                                      {categoria.nome}
                                    </p>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-40">
                        <div className="text-center">
                          <svg className="animate-spin h-8 w-8 mx-auto mb-2" style={{ color: '#96c925' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-sm text-gray-500">Carregando filtros...</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Drawer de Filtros da Categoria */}
                  {selectedFilterCategory && (
                    <div
                      className={`absolute inset-0 flex flex-col z-30 transform transition-transform duration-300 ease-in-out ${
                        showFilterDrawer
                          ? 'translate-x-0 pointer-events-auto'
                          : filterDrawerDirection === 'back'
                            ? '-translate-x-full pointer-events-none'
                            : 'translate-x-full pointer-events-none'
                      }`}
                      style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      {/* Header do Drawer */}
                      <div className="p-3 border-b" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              setFilterDrawerDirection('back');
                              setShowFilterDrawer(false);
                              setTimeout(() => {
                                setSelectedFilterCategory(null);
                                setFilterDrawerDirection('forward');
                              }, 300);
                            }}
                            className="p-1 rounded transition-colors hover:opacity-90"
                            title={t('common.back')}
                          >
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                              </svg>
                            </button>
                          <h3 className="text-white font-semibold" style={{ fontSize: '13px' }}>
                            {selectedFilterCategory.nome}
                          </h3>
                        </div>
                      </div>

                      {/* Lista de Filtros da Categoria */}
                      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: showFilterDrawer ? '100px' : '0' }}>
                        <div>
                          {selectedFilterCategory.filtros.map((filtro, index) => {
                            const thumbnailKey = `${selectedFilterCategory.nome}-${filtro.caminho}`;
                                    const thumbnailUrl = thumbnailCache.get(thumbnailKey);
                                    const isLoading = loadingThumbnails.has(thumbnailKey);
                            const fallbackImage = selectedImage ? selectedImage.src : null;
                            const previewImage = thumbnailUrl || (isLoading ? null : fallbackImage);

                                    return (
                              <button
                                        key={filtro.caminho}
                                onClick={() => handleApplyFilter(filtro)}
                                className="w-full px-3 py-2.5 transition-colors group hover:opacity-90"
                                style={{
                                  animationDelay: `${index * 50}ms`
                                }}
                              >
                                {previewImage ? (
                                  <div className="relative w-full">
                                    <img 
                                      src={previewImage} 
                                                alt={filtro.nome}
                                      className="w-full object-cover border" 
                                      style={{ 
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        height: '77px',
                                        minHeight: '77px',
                                        borderRadius: '12px'
                                      }} 
                                    />
                                    <div className="absolute inset-x-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/70 via-black/50 to-transparent" style={{ borderRadius: '0 0 12px 12px', paddingTop: '20px' }}>
                                      <p className="font-semibold text-white drop-shadow-lg" style={{ fontSize: '13px', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                                                  {filtro.nome}
                                                </p>
                                              </div>
                                  </div>
                                ) : (
                                    <div className="w-full flex items-center justify-center" style={{ 
                                      height: '77px',
                                      minHeight: '77px',
                                      backgroundColor: '#28380f',
                                      borderRadius: '12px'
                                    }}>
                                    <p className="font-semibold text-white" style={{ fontSize: '13px' }}>
                                      {filtro.nome}
                                    </p>
                                            </div>
                                          )}
                              </button>
                                    );
                                  })}
                                </div>
                              </div>

                      {/* Barra de Ações no rodapé - sempre visível quando está na tela de filtros individuais */}
                      {showFilterDrawer && (
                        <div className="border-t relative z-50 flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#222e0e' }}>
                          <EffectActionBar
                            onReset={() => {
                              if (originalImageBeforeFilter && selectedImageId) {
                                updateImage(selectedImageId, {
                                  ...originalImageBeforeFilter,
                                  image: originalImageBeforeFilter.image,
                                  originalImage: originalImageBeforeFilter.originalImage,
                                  analogFilter: null,
                                  analogFilterPath: null
                                });
                                setOriginalImageBeforeFilter(null);
                                setPreviewingFilter(null);
                                setSelectedFilter(null);
                              }
                            }}
                            onApply={() => {
                              // O filtro já está aplicado, apenas finaliza
                              // Não limpar previewingFilter para manter a barra visível
                              
                              // Invalidar thumbnails após confirmar aplicação do filtro
                              console.log('Filtro aplicado permanentemente, invalidando thumbnails...');
                              thumbnailCache.clear();
                              setLoadingThumbnails(new Set());
                              setOpenCategories(new Set()); // Fechar todas categorias para forçar regeneração
                              
                              // Marcar que o filtro foi aplicado permanentemente
                              setOriginalImageBeforeFilter(null);
                              setPreviewingFilter(null);
                            }}
                            isResetDisabled={!previewingFilter}
                            isApplyDisabled={!previewingFilter}
                            resetText={t('effects.resetFilters')}
                            applyText={t('common.apply')}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ABA CORES */}
              {activeTab === 'cores' && selectedImage && !isBaseLayerSelected && (
                <div className="h-full flex flex-col">
                  {/* Conteúdo principal */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Controles de Blend Mode */}
                    <div className="border-b border-gray-200 pb-3 pt-4 px-4">
                      <div className="space-y-3">
                        {/* Tipo */}
                        <div>
                          <label className="block text-xs font-medium text-white mb-2">
                            Tipo
                          </label>
                          <div className="flex gap-2">
                            {['multiply', 'overlay', 'color'].map((mode) => (
                              <label
                                key={mode}
                                className={`relative flex cursor-pointer rounded-lg px-3 py-2 transition-all ${
                                  colorBlendMode === mode
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="blend-mode"
                                  value={mode}
                                  checked={colorBlendMode === mode}
                                  onChange={(e) => {
                                    setColorBlendMode(e.target.value);
                                    if (selectedColorFilter && activeColorFilterType === 'gradient') {
                                      updateColorFilterSettings('gradient', {
                                        colors: selectedColorFilter.colors,
                                        angle: selectedColorFilter.angle || 135,
                                        opacity: colorOpacity,
                                        blendMode: e.target.value
                                      });
                                    }
                                  }}
                                  className="sr-only"
                                />
                                <span className="text-xs font-medium whitespace-nowrap">
                                  {t(`blendModes.${mode}`)}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Grid de filtros de cores */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {COLOR_FILTERS.map((colorFilter, index) => {
                          const gradient = `linear-gradient(${colorFilter.angle}deg, ${colorFilter.colors.join(', ')})`;
                          const isSelected = selectedColorFilter?.id === colorFilter.id;
                          const isLastInRow = index % 2 === 1; // É o último da linha (coluna 2)
                          
                          return (
                            <React.Fragment key={colorFilter.id}>
                              <button
                                onClick={() => handleSelectColorFilter(colorFilter)}
                                disabled={applyingFilter}
                                className={`group relative rounded-lg overflow-hidden border-2 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                                  isSelected
                                    ? 'border-green-600 ring-2 ring-green-600'
                                    : 'border-gray-200 hover:border-green-500'
                                }`}
                                style={{ aspectRatio: '1' }}
                              >
                                {/* Preview da imagem com filtro */}
                                <div className="absolute inset-0">
                                  {selectedImage && selectedImage.image ? (
                                    <canvas
                                      ref={(canvas) => {
                                        if (canvas && selectedImage.image) {
                                          const ctx = canvas.getContext('2d');
                                          canvas.width = 200;
                                          canvas.height = 200;
                                          
                                          const imgAspect = selectedImage.image.width / selectedImage.image.height;
                                          const canvasAspect = 200 / 200;
                                          
                                          let drawWidth, drawHeight, offsetX, offsetY;
                                          
                                          if (imgAspect > canvasAspect) {
                                            drawHeight = 200;
                                            drawWidth = 200 * imgAspect;
                                            offsetX = -(drawWidth - 200) / 2;
                                            offsetY = 0;
                                          } else {
                                            drawWidth = 200;
                                            drawHeight = 200 / imgAspect;
                                            offsetX = 0;
                                            offsetY = -(drawHeight - 200) / 2;
                                          }
                                          
                                          ctx.drawImage(selectedImage.image, offsetX, offsetY, drawWidth, drawHeight);
                                          
                                          // Se opacidade for 0, usar 1% internamente
                                          const effectiveOpacity = Math.max(1, colorOpacity);
                                          
                                          const angle = colorFilter.angle * Math.PI / 180;
                                          const grad = ctx.createLinearGradient(
                                            0, 0,
                                            Math.cos(angle) * 200,
                                            Math.sin(angle) * 200
                                          );
                                          
                                          colorFilter.colors.forEach((color, index) => {
                                            grad.addColorStop(index / (colorFilter.colors.length - 1), color);
                                          });
                                          
                                          ctx.globalCompositeOperation = colorBlendMode;
                                          ctx.globalAlpha = effectiveOpacity / 100;
                                          ctx.fillStyle = grad;
                                          ctx.fillRect(0, 0, 200, 200);
                                        }
                                      }}
                                      className="w-full h-full"
                                    />
                                  ) : (
                                    <div
                                      className="w-full h-full"
                                      style={{ background: gradient }}
                                    />
                                  )}
                                </div>
                                
                                {/* Nome do filtro */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/84 to-transparent p-2">
                                  <p className="text-white text-xs font-medium text-center">
                                    {colorFilter.name}
                                  </p>
                                </div>
                              </button>

                              {/* Slider de intensidade (aparece após a segunda coluna quando qualquer filtro da linha for selecionado) */}
                              {isLastInRow && (
                                (() => {
                                  const prevFilterIndex = index - 1;
                                  const prevFilter = COLOR_FILTERS[prevFilterIndex];
                                  const isPrevSelected = prevFilter && selectedColorFilter?.id === prevFilter.id;
                                  const isCurrentSelected = isSelected;
                                  const shouldShow = isPrevSelected || isCurrentSelected;
                                  const currentFilter = isPrevSelected ? prevFilter : (isCurrentSelected ? colorFilter : null);
                                  
                                  return shouldShow && currentFilter ? (
                                    <div className="col-span-2">
                                      <CustomSlider
                                        label="Intensidade"
                                        value={colorOpacity}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onChange={(newOpacity) => {
                                          setColorOpacity(newOpacity);
                                          updateColorFilterSettings('gradient', {
                                            colors: currentFilter.colors,
                                            angle: currentFilter.angle || 135,
                                            opacity: newOpacity,
                                            blendMode: colorBlendMode
                                          });
                                        }}
                                        unit="%"
                                        color="green"
                                        formatValue={(v) => Math.round(v)}
                                      />
                                    </div>
                                  ) : null;
                                })()
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* Botão de Configuração Personalizada */}
                      <button
                        onClick={() => {
                          if (selectedImageId && selectedImageId !== 'base-layer') {
                            openColorFiltersPanel(selectedImageId);
                            setActiveColorFilterType('gradient');
                            updateColorFilterSettings('gradient', {
                              colors: ['#FF6B6B', '#4ECDC4'],
                              angle: 135,
                              opacity: colorOpacity,
                              blendMode: colorBlendMode
                            });
                          }
                          setColorConfigOpen(true);
                          setTimeout(() => setShowColorConfig(true), 50);
                        }}
                        className="w-full border-2 border-dashed border-gray-300 hover:border-green-500 rounded-lg p-4 transition-colors group"
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                          </svg>
                          <span className="text-sm font-medium text-gray-600 group-hover:text-green-600">
                            {t('colorGradients.customConfig')}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Footer fixo com botões usando EffectActionBar para consistência */}
                  <EffectActionBar
                    onReset={() => {
                      setSelectedColorFilter(null);
                      setActiveColorFilterCanvas(null);
                    }}
                    onApply={async () => {
                      if (!selectedImageId || selectedImageId === 'base-layer' || !selectedColorFilter) return;
                      
                      setApplyingFilter(true);
                      
                      try {
                        console.log('Aplicando filtro de cor:', {
                          filtro: selectedColorFilter.name,
                          blendMode: colorBlendMode,
                          opacity: colorOpacity,
                          cores: selectedColorFilter.colors
                        });
                        
                        // Usar o sistema novo de lazy loading igual ao LayerPanel
                        await applyColorFilter(activeColorFilterCanvas);
                        
                        console.log('Filtro de cor aplicado com sucesso');
                        setSelectedColorFilter(null);
                        setApplyingFilter(false);
                        
                        // Invalidar thumbnails após aplicar cor permanentemente
                        console.log('Cor aplicada permanentemente, invalidando thumbnails...');
                        thumbnailCache.clear();
                        setLoadingThumbnails(new Set());
                        setOpenCategories(new Set()); // Fechar todas categorias
                      } catch (error) {
                        console.error('Erro ao aplicar filtro de cor:', error);
                        setApplyingFilter(false);
                      }
                    }}
                    isResetDisabled={!selectedColorFilter}
                    isApplyDisabled={applyingFilter || !selectedColorFilter}
                    resetText={t('effects.resetFilters')}
                    applyText={applyingFilter ? t('effects.applying') : t('common.apply')}
                  />
                </div>
              )}

              {/* ABA EFEITOS */}
              {activeTab === 'efeitos' && selectedImage && !isBaseLayerSelected && (
                <div className="h-full overflow-y-auto">
                  {/* Nível 1: Nenhum efeito selecionado - Mostrar seletor */}
                  {activeEffectType === null && (
                    <EffectSelector
                      onSelect={(type) => setActiveEffectType(type)}
                      onClose={() => {
                        closeEffectsPanel();
                      }}
                      onCategoryOpen={(cat) => setLastOpenedCategory(cat)}
                      initialCategory={lastOpenedCategory}
                      backToCategorySignal={backToCategorySignal}
                    />
                  )}

                  {/* Controles genéricos para todos os efeitos (exceto adjustments que já tem seu próprio controle) */}
                  {activeEffectType && activeEffectType !== 'adjustments' && (
                    <GenericEffectControls
                      effectId={activeEffectType}
                      settings={effectSettings[activeEffectType]}
                      onUpdate={(newSettings) => updateEffectSettings(activeEffectType, newSettings)}
                      onApply={() => {
                        applyEffect();
                        
                        // Invalidar thumbnails após aplicar efeito permanentemente
                        console.log('Efeito aplicado permanentemente, invalidando thumbnails...');
                        thumbnailCache.clear();
                        setLoadingThumbnails(new Set());
                        setOpenCategories(new Set()); // Fechar todas categorias
                      }}
                      onBack={() => {
                        setActiveEffectType(null);
                        setBackToCategorySignal((v) => v + 1);
                      }}
                      onReset={() => resetEffectSettings(activeEffectType)}
                    />
                  )}
                </div>
              )}

              {/* ABA PINTURA2 - Nova implementação baseada em SimplePaint */}
              {activeTab === 'pintura2' && selectedImage && !isBaseLayerSelected && (
                <div className="h-full flex flex-col">
                  {/* Controles de Pintura - Diretamente na Sidebar */}
                  <div className="flex-1 overflow-y-auto">
                    {console.log('🎨 [SidebarControls] Renderizando PaintingControls para Pintura2', { activeTab, selectedImage: !!selectedImage, isBaseLayerSelected })}
                    <PaintingControls
                      currentTool={paintCurrentTool}
                      onToolChange={(tool) => {
                        setPaintCurrentTool(tool);
                        setPaintingSettings(prev => ({ ...prev, currentTool: tool }));
                      }}
                      brushSize={paintBrushSize}
                      onBrushSizeChange={(size) => {
                        setPaintBrushSize(size);
                        setPaintingSettings(prev => ({ ...prev, brushSize: size }));
                      }}
                      brushColor={paintBrushColor}
                      onBrushColorChange={(color) => {
                        setPaintBrushColor(color);
                        setPaintingSettings(prev => ({ ...prev, brushColor: color }));
                      }}
                      brushOpacity={paintBrushOpacity}
                      onBrushOpacityChange={(opacity) => {
                        setPaintBrushOpacity(opacity);
                        setPaintingSettings(prev => ({ ...prev, brushOpacity: opacity }));
                      }}
                      brushFlow={paintBrushFlow}
                      onBrushFlowChange={(flow) => {
                        setPaintBrushFlow(flow);
                        setPaintingSettings(prev => ({ ...prev, brushFlow: flow }));
                      }}
                      brushSpacing={paintBrushSpacing}
                      onBrushSpacingChange={(spacing) => {
                        setPaintBrushSpacing(spacing);
                        setPaintingSettings(prev => ({ ...prev, brushSpacing: spacing }));
                      }}
                      brushAngle={paintBrushAngle}
                      onBrushAngleChange={(angle) => {
                        setPaintBrushAngle(angle);
                        setPaintingSettings(prev => ({ ...prev, brushAngle: angle }));
                      }}
                      brushRotate={paintBrushRotate}
                      onBrushRotateChange={(rotate) => {
                        setPaintBrushRotate(rotate);
                        setPaintingSettings(prev => ({ ...prev, brushRotate: rotate }));
                      }}
                      stabilizerLevel={paintStabilizerLevel}
                      onStabilizerLevelChange={(level) => {
                        setPaintStabilizerLevel(level);
                        setPaintingSettings(prev => ({ ...prev, stabilizerLevel: level }));
                      }}
                      stabilizerWeight={paintStabilizerWeight}
                      onStabilizerWeightChange={(weight) => {
                        setPaintStabilizerWeight(weight);
                        setPaintingSettings(prev => ({ ...prev, stabilizerWeight: weight }));
                      }}
                      onBrushImageSelect={(image) => {
                        setPaintCurrentBrushImage(image);
                        setPaintingSettings(prev => ({ ...prev, currentBrushImage: image }));
                      }}
                      currentBrushImage={paintCurrentBrushImage}
                      blendMode={paintBlendMode}
                      onBlendModeChange={(mode) => {
                        setPaintBlendMode(mode);
                        setPaintingSettings(prev => ({ ...prev, blendMode: mode }));
                      }}
                    />
                  </div>
                </div>
              )}

            </>
          )}
        </div>
      </div>

      {/* Drawer de Filtros Analógicos (overlay sobre o sidebar) */}
      <div className={`absolute inset-0 flex flex-col transform transition-transform duration-300 z-20 pointer-events-auto overflow-hidden ${filtersOpen && showFilters ? 'translate-x-0' : 'translate-x-full'}`} style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Header com botão voltar */}
        <div className={`p-3 border-b transition-transform duration-300 ease-in-out ${
          showFilterDrawer ? '-translate-x-full pointer-events-none' : 'translate-x-0 pointer-events-auto'
        }`} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center">
            <button
              onClick={() => {
                setShowFilters(false);
                setTimeout(() => setFiltersOpen(false), 250);
              }}
              className="p-1 rounded transition-colors hover:opacity-90"
              title="Voltar para detalhes da camada"
            >
              <ArrowLeftIcon className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Loading overlay ao aplicar filtro */}
        {applyingFilter && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 shadow-xl">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm font-medium text-gray-700">Aplicando filtro...</p>
              </div>
            </div>
          </div>
        )}

        {/* Lista de Categorias */}
        <div className={`flex-1 overflow-y-auto relative transition-transform duration-300 ease-in-out ${
          showFilterDrawer ? '-translate-x-full pointer-events-none' : 'translate-x-0 pointer-events-auto'
        }`}>
          {filtrosData ? (
            <div>
              {filtrosData.categorias
                .filter((categoria) => categoria.nome.toLowerCase() !== 'none')
                .map((categoria) => {
                  const firstFilter = categoria.filtros[0];
                  const thumbnailKey = firstFilter ? `${categoria.nome}-${firstFilter.caminho}` : null;
                  const thumbnailUrl = thumbnailKey ? thumbnailCache.get(thumbnailKey) : null;
                  const isLoading = thumbnailKey ? loadingThumbnails.has(thumbnailKey) : false;
                  const fallbackImage = selectedImage ? selectedImage.src : null;
                  const previewImage = thumbnailUrl || (isLoading ? null : fallbackImage);

                return (
                    <button
                      key={categoria.nome}
                      onClick={() => {
                        setSelectedFilterCategory(categoria);
                        setFilterDrawerDirection('forward');
                        setShowFilterDrawer(false);
                        // Usar requestAnimationFrame para garantir que o drawer seja renderizado fora da tela antes de animar
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            setShowFilterDrawer(true);
                          });
                        });
                      }}
                                className="w-full px-3 py-2.5 transition-colors group hover:opacity-90"
                              >
                      {previewImage ? (
                        <div className="relative w-full">
                          <img 
                            src={previewImage} 
                            alt={categoria.nome} 
                            className="w-full object-cover border" 
                            style={{ 
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        height: '77px',
                                        minHeight: '77px',
                                        borderRadius: '12px'
                            }} 
                          />
                                    <div className="absolute inset-x-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/70 via-black/50 to-transparent" style={{ borderRadius: '0 0 12px 12px', paddingTop: '20px' }}>
                                      <p className="font-semibold text-white drop-shadow-lg" style={{ fontSize: '13px', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                                  {categoria.nome}
                                      </p>
                              </div>
                        </div>
                      ) : (
                                    <div className="w-full flex items-center justify-center" style={{ 
                                      height: '77px',
                                      minHeight: '77px',
                                      backgroundColor: '#28380f',
                                      borderRadius: '12px'
                                    }}>
                          <p className="font-semibold text-white" style={{ fontSize: '16.8px' }}>
                            {categoria.nome}
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-gray-500">Carregando filtros...</p>
              </div>
            </div>
          )}

          {/* Drawer de Filtros da Categoria (no overlay) */}
          {selectedFilterCategory && (
            <div
              className={`absolute inset-0 flex flex-col z-30 transform transition-transform duration-300 ease-in-out ${
                showFilterDrawer
                  ? 'translate-x-0 pointer-events-auto'
                  : filterDrawerDirection === 'back'
                    ? '-translate-x-full pointer-events-none'
                    : 'translate-x-full pointer-events-none'
              }`}
              style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}
            >
              {/* Header do Drawer */}
              <div className="p-3 border-b" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setFilterDrawerDirection('back');
                      setShowFilterDrawer(false);
                      setTimeout(() => {
                        setSelectedFilterCategory(null);
                        setFilterDrawerDirection('forward');
                      }, 300);
                    }}
                    className="p-1 rounded transition-colors hover:opacity-90"
                    title={t('common.back')}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  <h3 className="text-white font-semibold" style={{ fontSize: '16.8px' }}>
                    {selectedFilterCategory.nome}
                  </h3>
                </div>
              </div>

              {/* Lista de Filtros da Categoria */}
              <div className="flex-1 overflow-y-auto" style={{ paddingBottom: showFilterDrawer ? '100px' : '0' }}>
                <div>
                  {selectedFilterCategory.filtros.map((filtro, index) => {
                    const thumbnailKey = `${selectedFilterCategory.nome}-${filtro.caminho}`;
                            const thumbnailUrl = thumbnailCache.get(thumbnailKey);
                            const isLoading = loadingThumbnails.has(thumbnailKey);
                    const fallbackImage = selectedImage ? selectedImage.src : null;
                    const previewImage = thumbnailUrl || (isLoading ? null : fallbackImage);

                            return (
                      <button
                                key={filtro.caminho}
                        onClick={() => handleApplyFilter(filtro)}
                        className="w-full px-3 py-2.5 transition-colors group hover:opacity-90"
                        style={{
                          animationDelay: `${index * 50}ms`
                        }}
                      >
                        {previewImage ? (
                          <div className="relative w-full">
                            <img 
                              src={previewImage} 
                                        alt={filtro.nome}
                              className="w-full object-cover border" 
                              style={{ 
                                        borderColor: 'rgba(255,255,255,0.1)',
                                        height: '77px',
                                        minHeight: '77px',
                                        borderRadius: '12px'
                              }} 
                            />
                                                                                                                                                                                                                                                                   <div className="absolute inset-x-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/70 via-black/50 to-transparent" style={{ borderRadius: '0 0 12px 12px', paddingTop: '20px' }}>
                                         <p className="font-semibold text-white drop-shadow-lg" style={{ fontSize: '13px', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                                              {filtro.nome}
                                            </p>
                                         </div>
                          </div>
                        ) : (
                                    <div className="w-full flex items-center justify-center" style={{ 
                                      height: '77px',
                                      minHeight: '77px',
                                      backgroundColor: '#28380f',
                                      borderRadius: '12px'
                                    }}>
                            <p className="font-semibold text-white" style={{ fontSize: '13px' }}>
                              {filtro.nome}
                            </p>
                      </div>
                    )}
                      </button>
                );
              })}
            </div>
              </div>

              {/* Barra de Ações no rodapé - sempre visível quando está na tela de filtros individuais */}
              {showFilterDrawer && (
                <div className="border-t relative z-50 flex-shrink-0" style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#222e0e' }}>
                  <EffectActionBar
                    onReset={() => {
                      if (originalImageBeforeFilter && selectedImageId) {
                        updateImage(selectedImageId, {
                          ...originalImageBeforeFilter,
                          image: originalImageBeforeFilter.image,
                          originalImage: originalImageBeforeFilter.originalImage,
                          analogFilter: null,
                          analogFilterPath: null
                        });
                        setOriginalImageBeforeFilter(null);
                        setPreviewingFilter(null);
                        setSelectedFilter(null);
                      }
                    }}
                    onApply={() => {
                      // O filtro já está aplicado, apenas finaliza
                      setOriginalImageBeforeFilter(null);
                      setPreviewingFilter(null);
                      
                      // Invalidar thumbnails após confirmar aplicação do filtro
                      console.log('Filtro aplicado permanentemente, invalidando thumbnails...');
                      thumbnailCache.clear();
                      setLoadingThumbnails(new Set());
                      setOpenCategories(new Set()); // Fechar todas categorias para forçar regeneração
                    }}
                    isResetDisabled={!previewingFilter}
                    isApplyDisabled={!previewingFilter}
                    resetText={t('effects.resetFilters')}
                    applyText={t('common.apply')}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Drawer de Filtros de Cores (overlay sobre o sidebar) */}
      <div className={`absolute inset-0 bg-white border-l border-gray-200 flex flex-col transform transition-transform duration-300 z-20 pointer-events-auto ${colorsOpen && showColors ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header com botão voltar */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center space-x-2 mb-1.5">
            <button
              onClick={() => {
                setShowColors(false);
                setTimeout(() => setColorsOpen(false), 250);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
              title="Voltar para detalhes da camada"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Aplique gradientes coloridos sobre sua imagem
          </p>
        </div>

        {/* Controles de Blend Mode - Sempre visível */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="space-y-3">
            {/* Tipo - Small Cards */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <div className="flex gap-2">
                {/* Multiplicação */}
                <label
                  className={`relative flex cursor-pointer rounded-lg px-3 py-2 transition-all ${
                    colorBlendMode === 'multiply'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="blend-mode"
                    value="multiply"
                    checked={colorBlendMode === 'multiply'}
                    onChange={(e) => {
                      setColorBlendMode(e.target.value);
                      // Atualizar settings do novo sistema
                      if (selectedColorFilter && activeColorFilterType === 'gradient') {
                        updateColorFilterSettings('gradient', {
                          colors: selectedColorFilter.colors,
                          angle: selectedColorFilter.angle || 135,
                          opacity: colorOpacity,
                          blendMode: e.target.value
                        });
                      }
                    }}
                    className="sr-only"
                  />
                  <span className="text-xs font-medium whitespace-nowrap">
                    {t('blendModes.multiply')}
                  </span>
                </label>

                {/* Sobreposição */}
                <label
                  className={`relative flex cursor-pointer rounded-lg px-3 py-2 transition-all ${
                    colorBlendMode === 'overlay'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="blend-mode"
                    value="overlay"
                    checked={colorBlendMode === 'overlay'}
                    onChange={(e) => {
                      setColorBlendMode(e.target.value);
                      // Atualizar settings do novo sistema
                      if (selectedColorFilter && activeColorFilterType === 'gradient') {
                        updateColorFilterSettings('gradient', {
                          colors: selectedColorFilter.colors,
                          angle: selectedColorFilter.angle || 135,
                          opacity: colorOpacity,
                          blendMode: e.target.value
                        });
                      }
                    }}
                    className="sr-only"
                  />
                  <span className="text-xs font-medium whitespace-nowrap">
                    {t('blendModes.overlay')}
                  </span>
                </label>

                {/* Cor */}
                <label
                  className={`relative flex cursor-pointer rounded-lg px-3 py-2 transition-all ${
                    colorBlendMode === 'color'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="blend-mode"
                    value="color"
                    checked={colorBlendMode === 'color'}
                    onChange={(e) => {
                      setColorBlendMode(e.target.value);
                      // Atualizar settings do novo sistema
                      if (selectedColorFilter && activeColorFilterType === 'gradient') {
                        updateColorFilterSettings('gradient', {
                          colors: selectedColorFilter.colors,
                          angle: selectedColorFilter.angle || 135,
                          opacity: colorOpacity,
                          blendMode: e.target.value
                        });
                      }
                    }}
                    className="sr-only"
                  />
                  <span className="text-xs font-medium whitespace-nowrap">
                    {t('blendModes.color')}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Área de scroll com os filtros */}
        <div className="flex-1 overflow-y-auto p-3">
          {/* Grid de filtros de cores com controle de intensidade integrado */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {COLOR_FILTERS.map((colorFilter, index) => {
              const gradient = `linear-gradient(${colorFilter.angle}deg, ${colorFilter.colors.join(', ')})`;
              const isSelected = selectedColorFilter?.id === colorFilter.id;
              const isLastInRow = index % 2 === 1; // É o último da linha (coluna 2)
              
              return (
                <React.Fragment key={colorFilter.id}>
                  <button
                    onClick={() => handleSelectColorFilter(colorFilter)}
                    disabled={applyingFilter}
                    className={`group relative rounded-lg overflow-hidden border-2 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                      isSelected
                        ? 'border-green-600 ring-2 ring-green-600'
                        : 'border-gray-200 hover:border-green-500'
                    }`}
                    style={{ aspectRatio: '1' }}
                  >
                    {/* Preview da imagem com filtro */}
                    <div className="absolute inset-0">
                      {selectedImage && selectedImage.image ? (
                        <canvas
                          ref={(canvas) => {
                            if (canvas && selectedImage.image) {
                              const ctx = canvas.getContext('2d');
                              canvas.width = 200;
                              canvas.height = 200;
                              
                              // Desenhar imagem preenchendo todo o espaço (cover)
                              const imgAspect = selectedImage.image.width / selectedImage.image.height;
                              const canvasAspect = 200 / 200;
                              
                              let drawWidth, drawHeight, offsetX, offsetY;
                              
                              if (imgAspect > canvasAspect) {
                                // Imagem mais larga - preencher altura
                                drawHeight = 200;
                                drawWidth = 200 * imgAspect;
                                offsetX = -(drawWidth - 200) / 2;
                                offsetY = 0;
                              } else {
                                // Imagem mais alta - preencher largura
                                drawWidth = 200;
                                drawHeight = 200 / imgAspect;
                                offsetX = 0;
                                offsetY = -(drawHeight - 200) / 2;
                              }
                              
                              ctx.drawImage(selectedImage.image, offsetX, offsetY, drawWidth, drawHeight);
                              
                              // Se opacidade for 0, usar 1% internamente
                              const effectiveOpacity = Math.max(1, colorOpacity);
                              
                              const angle = colorFilter.angle * Math.PI / 180;
                              const grad = ctx.createLinearGradient(
                                0, 0,
                                Math.cos(angle) * 200,
                                Math.sin(angle) * 200
                              );
                              
                              colorFilter.colors.forEach((color, index) => {
                                grad.addColorStop(index / (colorFilter.colors.length - 1), color);
                              });
                              
                              ctx.globalCompositeOperation = colorBlendMode;
                              ctx.globalAlpha = effectiveOpacity / 100;
                              ctx.fillStyle = grad;
                              ctx.fillRect(0, 0, 200, 200);
                            }
                          }}
                          className="w-full h-full"
                        />
                      ) : (
                        <div
                          className="w-full h-full"
                          style={{ background: gradient }}
                        />
                      )}
                    </div>
                    
                    {/* Nome do filtro */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/84 to-transparent p-2">
                      <p className="text-white text-xs font-medium text-center">
                        {colorFilter.name}
                      </p>
                    </div>
                    
                    {/* Loading overlay */}
                    {applyingFilter && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </button>
                  
                  {/* Controle de Intensidade - Aparece após a segunda coluna quando qualquer filtro da linha for selecionado */}
                  {isLastInRow && (
                    (() => {
                      const prevFilterIndex = index - 1;
                      const prevFilter = COLOR_FILTERS[prevFilterIndex];
                      const isPrevSelected = prevFilter && selectedColorFilter?.id === prevFilter.id;
                      const isCurrentSelected = isSelected;
                      const shouldShow = isPrevSelected || isCurrentSelected;
                      const currentFilter = isPrevSelected ? prevFilter : (isCurrentSelected ? colorFilter : null);
                      
                      return shouldShow && currentFilter ? (
                        <div className="col-span-2">
                          <CustomSlider
                            label="Intensidade"
                            value={colorOpacity}
                            min={0}
                            max={100}
                            step={1}
                            onChange={(newOpacity) => {
                              setColorOpacity(newOpacity);
                              // Atualizar settings do novo sistema em tempo real
                              if (activeColorFilterType === 'gradient') {
                                updateColorFilterSettings('gradient', {
                                  colors: currentFilter.colors,
                                  angle: currentFilter.angle || 135,
                                  opacity: newOpacity,
                                  blendMode: colorBlendMode
                                });
                              }
                            }}
                            unit="%"
                            color="green"
                            formatValue={(v) => Math.round(v)}
                          />
                        </div>
                      ) : null;
                    })()
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Botão de Configuração Personalizada */}
          <button
            onClick={() => {
              // Ativar sistema de lazy loading ao abrir gradiente personalizado
              if (selectedImageId && selectedImageId !== 'base-layer') {
                openColorFiltersPanel(selectedImageId);
                setActiveColorFilterType('gradient');
                // Configurar com cores padrão inicial
                updateColorFilterSettings('gradient', {
                  colors: ['#FF6B6B', '#4ECDC4'],
                  angle: 135,
                  opacity: colorOpacity,
                  blendMode: colorBlendMode
                });
              }
              setColorConfigOpen(true);
              setTimeout(() => setShowColorConfig(true), 50);
            }}
            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span className="text-sm font-medium text-gray-600 group-hover:text-green-600">
                Configuração Personalizada
              </span>
            </div>
          </button>
        </div>

        {/* Botões de Ação - Só mostra quando filtro selecionado */}
        {selectedColorFilter && (
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <button
                onClick={handleApplySelectedFilter}
                disabled={applyingFilter}
                className="flex-1 flex items-center justify-center space-x-2 p-3 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applyingFilter ? (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className="text-sm font-medium">
                  {applyingFilter ? t('effects.applying') : t('effects.applyFilter')}
                </span>
              </button>
              
              <button
                onClick={() => {
                  setSelectedColorFilter(null);
                  setActiveColorFilterCanvas(null);
                }}
                className="px-3 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
                title="Limpar seleção"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer de Efeitos (overlay sobre o sidebar) */}
      <div className={`absolute inset-0 bg-white flex flex-col transform transition-transform duration-300 z-30 ${effectsOpen && showEffects ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header com botão voltar */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center space-x-2 mb-1.5">
            <button
              onClick={() => {
                setShowEffects(false);
                setTimeout(() => {
                  setEffectsOpen(false);
                  closeEffectsPanel();
                }, 250);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors focus-visible:ring-2 focus-visible:ring-blue-500"
              title="Voltar para detalhes da camada"
              aria-label="Voltar"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500">Aplique efeitos especiais</p>
        </div>

        {/* Conteúdo dos efeitos */}
        <div className="flex-1 overflow-y-auto">
          {/* Aqui você pode adicionar o conteúdo dos efeitos */}
          <div className="text-center py-8 p-4">
            <p className="text-sm text-gray-600">Efeitos em desenvolvimento</p>
          </div>
        </div>
      </div>

      {/* Modal de Configuração Personalizada de Cores */}
      {colorConfigOpen && showColorConfig && (
        <ColorConfigDrawer
          onClose={() => {
            setShowColorConfig(false);
            setTimeout(() => setColorConfigOpen(false), 250);
          }}
          onApply={(customFilter) => {
            setSelectedColorFilter(customFilter);
            setShowColorConfig(false);
            setTimeout(() => setColorConfigOpen(false), 250);
          }}
          colorBlendMode={colorBlendMode}
          colorOpacity={colorOpacity}
          updateColorFilterSettings={updateColorFilterSettings}
        />
      )}

      {/* Modal de Confirmação para Conflitos - Renderizado via Portal */}
      {showConfirmationModal && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" 
          style={{ zIndex: 9999 }}
        >
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                {confirmationType === 'filter' 
                  ? 'Deseja manter a alteração de filtro?' 
                  : confirmationType === 'color'
                  ? 'Deseja manter a alteração de cor?'
                  : 'Deseja manter a alteração de efeito?'}
              </h3>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelAction}
                  className="flex-1 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Não
                </button>
                <button
                  onClick={confirmAction}
                  className="flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  style={{ backgroundColor: '#96c925', color: '#000' }}
                >
                  Sim
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Pintura Completo */}
      <PaintingModal
        isOpen={paintingTestModalOpen}
        onClose={() => setPaintingTestModalOpen(false)}
        sourceImage={selectedImage?.image}
        onApply={(paintedImage) => {
          // Aplicar imagem pintada
          if (selectedImage) {
            updateImage(selectedImageId, {
              ...selectedImage,
              image: paintedImage,
              src: paintedImage.src
            });
            saveHistoryState();
          }
          setPaintingTestModalOpen(false);
        }}
      />
    </div>
  );
};

export default SidebarControls;

